import { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Heart, FileText, Syringe, PawPrint, Camera, Sparkle, Bell,
    Clock, CheckCircle, Warning, Plus, Trash, PencilSimple,
    UploadSimple, X, Certificate, Pill, Stethoscope, CalendarBlank,
} from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

/* ─────────────────────────────────────────────────────────────
 *  HealthHub — the "Pet Health & Care Hub" experience.
 *  Reuses the existing pet document upload + care reminder APIs
 *  and adds the new structured health/vaccination endpoints.
 * ────────────────────────────────────────────────────────────*/

/** Small helper: any of these fields being set means the pet has "started"
 *  building a real health record — so we hide the first-time setup card. */
function _hasHealthData(pet) {
    if (!pet) return false;
    if ((pet.vaccinations || []).length > 0) return true;
    if ((pet.documents || []).length > 0) return true;
    return Boolean(
        pet.microchip_id || pet.passport_id || pet.birth_date ||
        pet.allergies || pet.medical_notes ||
        (pet.conditions || []).length > 0 ||
        (pet.medications || []).length > 0 ||
        (pet.veterinarian && (pet.veterinarian.name || pet.veterinarian.clinic))
    );
}

function _fmtDate(v, locale) {
    if (!v) return "";
    try { return new Date(v).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return String(v); }
}

/** Confidence buckets from the OCR endpoint. */
function _isLowConfidence(conf) {
    return typeof conf === "number" && conf < 0.6;
}

export default function HealthHub({ pet, onChange }) {
    const { t, lang } = useI18n();
    const [reminders, setReminders] = useState({ upcoming: [], overdue: [], completed: [] });
    const [showManual, setShowManual] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [extractionPreview, setExtractionPreview] = useState(null);
    const hasData = _hasHealthData(pet);

    const loadReminders = async () => {
        try {
            const { data } = await api.get("/reminders/mine");
            const mine = {
                upcoming: (data.upcoming || []).filter((r) => r.pet_id === pet.id),
                overdue:  (data.overdue  || []).filter((r) => r.pet_id === pet.id),
                completed:(data.completed|| []).filter((r) => r.pet_id === pet.id),
            };
            setReminders(mine);
        } catch { /* ignore */ }
    };

    useEffect(() => { loadReminders(); /* eslint-disable-next-line */ }, [pet?.id]);

    return (
        <section data-testid="health-hub" className="mt-6 space-y-5">
            {/* Cover card — reuses the existing passport visual identity */}
            <HeaderCard pet={pet} t={t} />

            {/* First-time setup — only when the record is empty */}
            {!hasData && !showManual && !showScanner && (
                <FirstTimeSetup
                    t={t}
                    onManual={() => setShowManual(true)}
                    onScan={() => setShowScanner(true)}
                />
            )}

            {/* Health overview snapshot (once any data exists) */}
            {hasData && (
                <Overview
                    pet={pet}
                    t={t} lang={lang}
                    remindersCount={reminders.upcoming.length + reminders.overdue.length}
                />
            )}

            {/* Identity */}
            <ManualEditor
                pet={pet} onSaved={onChange}
                open={showManual || hasData}
                onClose={() => setShowManual(false)}
                t={t}
            />

            {/* Vaccinations & appointments */}
            <VaccinationSection
                pet={pet} onChange={onChange}
                reminders={reminders} onReminderChange={loadReminders}
                t={t} lang={lang}
            />

            <AppointmentSection
                pet={pet} onChange={onChange}
                reminders={reminders} onReminderChange={loadReminders}
                t={t} lang={lang}
            />

            {/* Documents (reuses existing pet.documents API) */}
            <DocumentsSection
                pet={pet} onChange={onChange}
                onScan={() => setShowScanner(true)}
                t={t} lang={lang}
            />

            {/* Timeline */}
            {hasData && <TimelineSection pet={pet} t={t} lang={lang} />}

            {/* Scanner + OCR review overlays */}
            <AnimatePresence>
                {showScanner && (
                    <DocumentScannerModal
                        pet={pet}
                        onClose={() => setShowScanner(false)}
                        onExtracted={(payload) => { setShowScanner(false); setExtractionPreview(payload); }}
                        t={t}
                    />
                )}
                {extractionPreview && (
                    <ExtractionReviewModal
                        pet={pet}
                        payload={extractionPreview}
                        onClose={() => setExtractionPreview(null)}
                        onSaved={async () => { setExtractionPreview(null); await onChange(); await loadReminders(); }}
                        t={t}
                    />
                )}
            </AnimatePresence>
        </section>
    );
}

/* ---------- Header cover ---------- */
function HeaderCard({ pet, t }) {
    return (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-paw-forest via-paw-forest to-paw-forestDark text-white shadow-hover" data-testid="health-hub-cover">
            <div aria-hidden className="absolute -top-16 -end-10 w-56 h-56 rounded-full bg-paw-terra/25 blur-3xl" />
            <div aria-hidden className="absolute -bottom-20 -start-10 w-64 h-64 rounded-full bg-white/8 blur-3xl" />
            <div className="relative p-6 sm:p-8 flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl overflow-hidden ring-2 ring-white/40 flex-shrink-0 bg-white/10">
                    {pet.photo_path
                        ? <img src={fileUrl(pet.photo_path)} alt={pet.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full grid place-items-center"><PawPrint size={32} weight="duotone" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-paw-terra/95 font-bold">{t("health.kicker")}</p>
                    <h2 className="font-display text-2xl sm:text-3xl leading-tight mt-0.5 truncate">{pet.name}</h2>
                    <p className="text-xs text-white/70 mt-1">ID · {(pet.public_id || "").slice(0, 8).toUpperCase()}</p>
                </div>
                <Heart size={28} weight="fill" className="text-paw-terra flex-shrink-0 hidden sm:block" />
            </div>
        </div>
    );
}

/* ---------- First-time setup: two friendly cards ---------- */
function FirstTimeSetup({ t, onManual, onScan }) {
    return (
        <section data-testid="health-first-time" className="rounded-3xl border border-paw-line dark:border-white/10 bg-gradient-to-br from-paw-terra/8 via-white to-paw-forest/10 dark:from-paw-terra/16 dark:via-white/[0.03] dark:to-paw-forest/16 p-5 sm:p-6 space-y-4 shadow-soft">
            <div>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-paw-terra">{t("health.setup_kicker")}</p>
                <h3 className="font-display text-xl sm:text-2xl text-paw-ink dark:text-paw-bone mt-1">{t("health.setup_h")}</h3>
                <p className="text-sm text-paw-ink2 dark:text-paw-bone/70 mt-1">{t("health.setup_sub")}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                <button
                    type="button" onClick={onManual}
                    data-testid="health-setup-manual"
                    className="group text-start rounded-2xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.04] hover:bg-paw-muted dark:hover:bg-white/[0.07] p-4 transition"
                >
                    <div className="w-11 h-11 rounded-2xl bg-paw-forest/10 text-paw-forest dark:bg-paw-terra/15 dark:text-paw-terra grid place-items-center mb-2.5">
                        <PencilSimple size={20} weight="fill" />
                    </div>
                    <p className="font-semibold text-paw-ink dark:text-paw-bone">{t("health.setup_manual_h")}</p>
                    <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 mt-0.5">{t("health.setup_manual_sub")}</p>
                </button>
                <button
                    type="button" onClick={onScan}
                    data-testid="health-setup-scan"
                    className="group text-start rounded-2xl border border-paw-terra/40 bg-gradient-to-br from-paw-terra/12 to-paw-forest/8 dark:from-paw-terra/25 dark:to-paw-forest/18 hover:brightness-105 p-4 transition"
                >
                    <div className="w-11 h-11 rounded-2xl bg-paw-terra text-white grid place-items-center mb-2.5 shadow-soft">
                        <Camera size={20} weight="fill" />
                    </div>
                    <p className="font-semibold text-paw-ink dark:text-paw-bone flex items-center gap-1.5">
                        {t("health.setup_scan_h")}
                        <span className="text-[9px] uppercase tracking-wider bg-paw-terra text-white px-1.5 py-0.5 rounded-full">{t("health.ai_badge")}</span>
                    </p>
                    <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 mt-0.5">{t("health.setup_scan_sub")}</p>
                </button>
            </div>
        </section>
    );
}

/* ---------- Overview snapshot ---------- */
function Overview({ pet, t, lang, remindersCount }) {
    const vaccCount = (pet.vaccinations || []).length;
    const docCount = (pet.documents || []).length;
    const chips = [
        { icon: Syringe, tone: "rose", value: vaccCount, label: t("health.stat_vaccinations") },
        { icon: FileText, tone: "sky", value: docCount, label: t("health.stat_documents") },
        { icon: Bell, tone: "amber", value: remindersCount, label: t("health.stat_reminders") },
    ];
    const tones = {
        rose:  "bg-rose-500/12 text-rose-700 dark:text-rose-300",
        sky:   "bg-sky-500/12 text-sky-700 dark:text-sky-300",
        amber: "bg-amber-500/12 text-amber-800 dark:text-amber-300",
    };
    return (
        <section data-testid="health-overview" className="rounded-3xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 sm:p-5 shadow-soft">
            <h3 className="text-[11px] uppercase tracking-widest font-bold text-paw-ink dark:text-paw-bone mb-3">{t("health.overview_h")}</h3>
            <div className="grid grid-cols-3 gap-2">
                {chips.map((c) => (
                    <div key={c.label} className={`rounded-2xl px-3 py-2 flex items-center gap-2 ${tones[c.tone]}`}>
                        <c.icon size={16} weight="fill" />
                        <div className="min-w-0">
                            <div className="text-sm font-bold leading-none">{c.value}</div>
                            <div className="text-[10px] uppercase tracking-wider opacity-80">{c.label}</div>
                        </div>
                    </div>
                ))}
            </div>
            {pet.updated_at && (
                <p className="mt-2 text-[10px] text-paw-ink2 dark:text-paw-bone/50">
                    {t("health.last_updated", { at: _fmtDate(pet.updated_at, lang) })}
                </p>
            )}
        </section>
    );
}

/* ---------- Progressive manual editor (Identity + Health + Vet) ---------- */
function ManualEditor({ pet, onSaved, open, onClose, t }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [state, setState] = useState(() => ({
        birth_date: pet.birth_date || "",
        sex: pet.sex || pet.gender || "",
        microchip_id: pet.microchip_id || "",
        passport_id: pet.passport_id || "",
        allergies: pet.allergies || "",
        medical_notes: pet.medical_notes || "",
        conditions: (pet.conditions || []).join(", "),
        medications: (pet.medications || []).join(", "),
        vet_name:   pet.veterinarian?.name || "",
        vet_phone:  pet.veterinarian?.phone || "",
        vet_clinic: pet.veterinarian?.clinic || "",
    }));

    useEffect(() => {
        setState({
            birth_date: pet.birth_date || "",
            sex: pet.sex || pet.gender || "",
            microchip_id: pet.microchip_id || "",
            passport_id: pet.passport_id || "",
            allergies: pet.allergies || "",
            medical_notes: pet.medical_notes || "",
            conditions: (pet.conditions || []).join(", "),
            medications: (pet.medications || []).join(", "),
            vet_name:   pet.veterinarian?.name || "",
            vet_phone:  pet.veterinarian?.phone || "",
            vet_clinic: pet.veterinarian?.clinic || "",
        });
    }, [pet]);

    if (!open) return null;

    const save = async () => {
        setBusy(true); setErr("");
        try {
            const payload = {
                birth_date: state.birth_date || null,
                sex: state.sex || null,
                microchip_id: state.microchip_id || null,
                passport_id: state.passport_id || null,
                allergies: state.allergies || null,
                medical_notes: state.medical_notes || null,
                conditions: state.conditions ? state.conditions.split(",").map((s) => s.trim()).filter(Boolean) : [],
                medications: state.medications ? state.medications.split(",").map((s) => s.trim()).filter(Boolean) : [],
                veterinarian: {
                    name:   state.vet_name || null,
                    phone:  state.vet_phone || null,
                    clinic: state.vet_clinic || null,
                },
            };
            await api.patch(`/pets/${pet.id}/health`, payload);
            await onSaved();
            onClose?.();
        } catch (e) {
            setErr(e?.response?.data?.detail || t("health.save_error"));
        } finally { setBusy(false); }
    };

    return (
        <details className="rounded-2xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.03] shadow-soft" open data-testid="health-manual">
            <summary className="px-4 py-3 cursor-pointer flex items-center gap-2 select-none">
                <PencilSimple size={16} weight="fill" className="text-paw-forest dark:text-paw-terra" />
                <span className="font-semibold text-paw-ink dark:text-paw-bone">{t("health.manual_h")}</span>
            </summary>
            <div className="px-4 pb-4 space-y-4">

                <Fieldset title={t("health.sec_identification")} testId="health-sec-identification">
                    <TwoCol>
                        <TextField id="birth_date" type="date" label={t("health.f_birth_date")} value={state.birth_date} onChange={(v) => setState((s) => ({ ...s, birth_date: v }))} />
                        <SelectField id="sex" label={t("health.f_sex")} value={state.sex} onChange={(v) => setState((s) => ({ ...s, sex: v }))} options={[
                            { value: "", labelKey: "health.sex_unset" },
                            { value: "male", labelKey: "health.sex_male" },
                            { value: "female", labelKey: "health.sex_female" },
                            { value: "unknown", labelKey: "health.sex_unknown" },
                        ]} t={t} />
                    </TwoCol>
                    <TwoCol>
                        <TextField id="microchip_id" label={t("health.f_microchip")} placeholder={t("health.f_microchip_ph")} value={state.microchip_id} onChange={(v) => setState((s) => ({ ...s, microchip_id: v }))} />
                        <TextField id="passport_id" label={t("health.f_passport_id")} placeholder={t("health.f_passport_id_ph")} value={state.passport_id} onChange={(v) => setState((s) => ({ ...s, passport_id: v }))} />
                    </TwoCol>
                </Fieldset>

                <Fieldset title={t("health.sec_health")} testId="health-sec-health">
                    <TextArea id="allergies" label={t("health.f_allergies")} placeholder={t("health.f_allergies_ph")} value={state.allergies} onChange={(v) => setState((s) => ({ ...s, allergies: v }))} />
                    <TextArea id="conditions" label={t("health.f_conditions")} placeholder={t("health.f_conditions_ph")} value={state.conditions} onChange={(v) => setState((s) => ({ ...s, conditions: v }))} />
                    <TextArea id="medications" label={t("health.f_medications")} placeholder={t("health.f_medications_ph")} value={state.medications} onChange={(v) => setState((s) => ({ ...s, medications: v }))} />
                    <TextArea id="medical_notes" label={t("health.f_medical_notes")} placeholder={t("health.f_medical_notes_ph")} value={state.medical_notes} onChange={(v) => setState((s) => ({ ...s, medical_notes: v }))} />
                </Fieldset>

                <Fieldset title={t("health.sec_vet")} testId="health-sec-vet">
                    <TwoCol>
                        <TextField id="vet_name"   label={t("health.f_vet_name")}   value={state.vet_name}   onChange={(v) => setState((s) => ({ ...s, vet_name: v }))} />
                        <TextField id="vet_phone"  label={t("health.f_vet_phone")}  value={state.vet_phone}  onChange={(v) => setState((s) => ({ ...s, vet_phone: v }))} />
                    </TwoCol>
                    <TextField id="vet_clinic" label={t("health.f_vet_clinic")} value={state.vet_clinic} onChange={(v) => setState((s) => ({ ...s, vet_clinic: v }))} />
                </Fieldset>

                {err && <p className="text-xs text-red-600" data-testid="health-manual-error">{err}</p>}
                <div className="flex justify-end pt-1">
                    <button type="button" onClick={save} disabled={busy}
                        data-testid="health-manual-save"
                        className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-5 h-10 text-sm font-semibold disabled:opacity-60">
                        {busy ? t("health.saving") : t("health.save")}
                    </button>
                </div>
                <p className="text-[10px] text-paw-ink2 dark:text-paw-bone/50">
                    {t("health.no_diagnosis_note")}
                </p>
            </div>
        </details>
    );
}

function Fieldset({ title, children, testId }) {
    return (
        <fieldset data-testid={testId} className="rounded-xl border border-paw-line dark:border-white/10 p-3 space-y-2.5">
            <legend className="text-[10px] uppercase tracking-widest font-bold text-paw-ink2 dark:text-paw-bone/60 px-1">{title}</legend>
            {children}
        </fieldset>
    );
}
function TwoCol({ children }) { return <div className="grid gap-2.5 sm:grid-cols-2">{children}</div>; }
function TextField({ id, label, value, onChange, placeholder, type = "text" }) {
    return (
        <label className="block">
            <span className="label-soft">{label}</span>
            <input
                id={id} type={type}
                data-testid={`health-input-${id}`}
                value={value || ""} placeholder={placeholder || ""}
                onChange={(e) => onChange(e.target.value)}
                className="input-soft mt-1.5 w-full"
            />
        </label>
    );
}
function TextArea({ id, label, value, onChange, placeholder }) {
    return (
        <label className="block">
            <span className="label-soft">{label}</span>
            <textarea
                id={id} rows={2}
                data-testid={`health-input-${id}`}
                value={value || ""} placeholder={placeholder || ""}
                onChange={(e) => onChange(e.target.value)}
                className="input-soft mt-1.5 w-full resize-y"
            />
        </label>
    );
}
function SelectField({ id, label, value, onChange, options, t }) {
    return (
        <label className="block">
            <span className="label-soft">{label}</span>
            <select
                id={id}
                data-testid={`health-input-${id}`}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="input-soft mt-1.5 w-full"
            >
                {options.map((o) => (<option key={o.value} value={o.value}>{t(o.labelKey)}</option>))}
            </select>
        </label>
    );
}

/* ---------- Vaccinations section (list + add + reminders) ---------- */
function VaccinationSection({ pet, onChange, reminders, onReminderChange, t, lang }) {
    const [adding, setAdding] = useState(false);
    const list = pet.vaccinations || [];

    return (
        <section data-testid="health-vaccinations" className="rounded-3xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 sm:p-5 shadow-soft space-y-3">
            <div className="flex items-center gap-2">
                <Syringe size={16} weight="fill" className="text-rose-500" />
                <h3 className="text-[11px] uppercase tracking-widest font-bold text-paw-ink dark:text-paw-bone flex-1">{t("health.sec_vaccinations")}</h3>
                <button
                    type="button" onClick={() => setAdding((v) => !v)}
                    data-testid="health-vacc-add-toggle"
                    className="inline-flex items-center gap-1 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white text-[11px] font-semibold px-3 h-7"
                >
                    <Plus size={12} weight="bold" />
                    {t("health.vacc_add")}
                </button>
            </div>

            {adding && <VaccinationForm pet={pet} onSaved={async () => { setAdding(false); await onChange(); }} onCancel={() => setAdding(false)} t={t} />}

            {list.length === 0 ? (
                <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 py-2" data-testid="health-vacc-empty">{t("health.vacc_empty")}</p>
            ) : (
                <ul className="space-y-2" data-testid="health-vacc-list">
                    {list.map((v) => (
                        <VaccinationRow
                            key={v.id} pet={pet} vacc={v}
                            onChange={onChange}
                            reminders={reminders}
                            onReminderChange={onReminderChange}
                            t={t} lang={lang}
                        />
                    ))}
                </ul>
            )}

            {/* Reminders — non-medical care reminders scoped to this pet */}
            <div className="pt-3 border-t border-paw-line dark:border-white/10">
                <div className="flex items-center gap-2 mb-1.5">
                    <Bell size={14} weight="fill" className="text-amber-500" />
                    <h4 className="text-[11px] uppercase tracking-widest font-bold text-paw-ink dark:text-paw-bone">{t("health.sec_reminders")}</h4>
                </div>
                <ReminderStack list={reminders} onChange={onReminderChange} t={t} lang={lang} />
            </div>
        </section>
    );
}

function VaccinationForm({ pet, prefill = {}, onSaved, onCancel, t }) {
    const [state, setState] = useState(() => ({
        name: prefill.name || "",
        dose_date: prefill.dose_date || "",
        next_due_date: prefill.next_due_date || "",
        veterinarian: prefill.veterinarian || "",
        clinic: prefill.clinic || "",
        batch_number: prefill.batch_number || "",
        note: prefill.note || "",
    }));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const save = async () => {
        if (!state.name.trim()) { setErr(t("health.vacc_name_req")); return; }
        setBusy(true); setErr("");
        try {
            const payload = Object.fromEntries(Object.entries(state).filter(([, v]) => v !== ""));
            await api.post(`/pets/${pet.id}/vaccinations`, payload);
            await onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || t("health.save_error"));
        } finally { setBusy(false); }
    };
    return (
        <div className="rounded-2xl border border-paw-terra/25 bg-paw-terra/6 p-3 space-y-2" data-testid="health-vacc-form">
            <TextField id="vacc_name" label={t("health.f_vacc_name")} value={state.name} onChange={(v) => setState((s) => ({ ...s, name: v }))} />
            <TwoCol>
                <TextField id="vacc_dose" type="date" label={t("health.f_dose_date")} value={state.dose_date} onChange={(v) => setState((s) => ({ ...s, dose_date: v }))} />
                <TextField id="vacc_next" type="date" label={t("health.f_next_due")} value={state.next_due_date} onChange={(v) => setState((s) => ({ ...s, next_due_date: v }))} />
            </TwoCol>
            <TwoCol>
                <TextField id="vacc_vet"    label={t("health.f_vet_name")}   value={state.veterinarian} onChange={(v) => setState((s) => ({ ...s, veterinarian: v }))} />
                <TextField id="vacc_clinic" label={t("health.f_vet_clinic")} value={state.clinic}       onChange={(v) => setState((s) => ({ ...s, clinic: v }))} />
            </TwoCol>
            <TextField id="vacc_batch" label={t("health.f_batch")} value={state.batch_number} onChange={(v) => setState((s) => ({ ...s, batch_number: v }))} />
            <TextArea  id="vacc_note"  label={t("health.f_note")}  value={state.note}          onChange={(v) => setState((s) => ({ ...s, note: v }))} />
            {err && <p className="text-xs text-red-600">{err}</p>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onCancel} className="rounded-full border border-paw-line dark:border-white/15 px-4 h-9 text-xs">{t("health.cancel")}</button>
                <button type="button" onClick={save} disabled={busy}
                    data-testid="health-vacc-save"
                    className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-4 h-9 text-xs font-semibold disabled:opacity-60">
                    {busy ? t("health.saving") : t("health.save")}
                </button>
            </div>
        </div>
    );
}

function VaccinationRow({ pet, vacc, onChange, reminders, onReminderChange, t, lang }) {
    const [busy, setBusy] = useState(false);
    const [dlg, setDlg] = useState(false);

    // A reminder already exists for THIS vaccination iff its source
    // identity matches: (source_type=vaccination, source_id=vacc.id).
    const linkedReminder = useMemo(() => {
        const all = [
            ...(reminders?.upcoming || []),
            ...(reminders?.overdue || []),
            ...(reminders?.completed || []),
        ];
        return all.find((r) => r.source_type === "vaccination" && r.source_id === vacc.id) || null;
    }, [reminders, vacc.id]);

    const remove = async () => {
        if (!window.confirm(t("health.confirm_remove_vacc", { name: vacc.name }))) return;
        setBusy(true);
        try { await api.delete(`/pets/${pet.id}/vaccinations/${vacc.id}`); await onChange(); }
        finally { setBusy(false); }
    };

    const canSuggestReminder = Boolean(vacc.next_due_date) && !linkedReminder;

    return (
        <li data-testid={`health-vacc-${vacc.id}`} className="rounded-2xl border border-paw-line dark:border-white/10 p-3 space-y-2">
            <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-2xl bg-rose-500/12 text-rose-600 dark:text-rose-300 grid place-items-center flex-shrink-0"><Syringe size={16} weight="fill" /></span>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-paw-ink dark:text-paw-bone truncate">{vacc.name}</p>
                    <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60 truncate">
                        {vacc.dose_date && <span>{t("health.dose_short")}: {_fmtDate(vacc.dose_date, lang)}</span>}
                        {vacc.next_due_date && <span> · {t("health.next_short")}: {_fmtDate(vacc.next_due_date, lang)}</span>}
                        {vacc.clinic && <span> · {vacc.clinic}</span>}
                    </p>
                </div>
                <button type="button" onClick={remove} disabled={busy}
                    data-testid={`health-vacc-remove-${vacc.id}`}
                    className="w-8 h-8 rounded-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 grid place-items-center flex-shrink-0"><Trash size={14} /></button>
            </div>

            {/* One-tap reminder suggestion — user MUST confirm before we create */}
            {canSuggestReminder && (
                <div
                    className="rounded-xl bg-amber-500/10 border border-amber-500/25 px-3 py-2 flex items-center gap-2"
                    data-testid={`health-vacc-remind-suggest-${vacc.id}`}
                >
                    <Bell size={14} weight="fill" className="text-amber-600 dark:text-amber-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-amber-900 dark:text-amber-100 truncate">{t("health.rem_suggest_h_vaccination")}</p>
                        <p className="text-[10px] text-amber-800/80 dark:text-amber-200/70 truncate">{t("health.rem_suggest_sub")}</p>
                    </div>
                    <button
                        type="button" onClick={() => setDlg(true)}
                        data-testid={`health-vacc-remind-open-${vacc.id}`}
                        className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold px-3 h-7 flex-shrink-0"
                    >
                        {t("health.rem_suggest_open")}
                    </button>
                </div>
            )}
            {linkedReminder && (
                <p
                    className="text-[10.5px] text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"
                    data-testid={`health-vacc-remind-linked-${vacc.id}`}
                >
                    <CheckCircle size={12} weight="fill" /> {t("health.rem_suggest_existing")} · {_fmtDate(linkedReminder.due_at, lang)}
                </p>
            )}

            <AnimatePresence>
                {dlg && (
                    <ReminderConfirmDialog
                        pet={pet}
                        source={{
                            type: "vaccination",
                            id: vacc.id,
                            reminder_type: "vaccination",
                            default_title: `${vacc.name} — ${t("health.next_short")}`,
                            default_date: vacc.next_due_date,
                            default_note: vacc.batch_number ? `Batch ${vacc.batch_number}` : "",
                        }}
                        onClose={() => setDlg(false)}
                        onCreated={async () => { setDlg(false); await onReminderChange(); }}
                        t={t}
                    />
                )}
            </AnimatePresence>
        </li>
    );
}

function ReminderStack({ list, onChange, t, lang }) {
    const buckets = [
        { key: "overdue",   labelKey: "health.rem_overdue",   items: list.overdue,   tone: "bg-red-500/15 text-red-700 dark:text-red-300", icon: Warning },
        { key: "upcoming",  labelKey: "health.rem_due_soon",  items: list.upcoming,  tone: "bg-amber-500/15 text-amber-800 dark:text-amber-300", icon: Clock },
        { key: "completed", labelKey: "health.rem_completed", items: list.completed, tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", icon: CheckCircle },
    ];
    const anything = buckets.some((b) => b.items.length > 0);
    if (!anything) return <p className="text-xs text-paw-ink2 dark:text-paw-bone/60" data-testid="health-rem-empty">{t("health.rem_empty")}</p>;
    return (
        <div className="space-y-2" data-testid="health-rem-stack">
            {buckets.map((b) => b.items.length > 0 && (
                <div key={b.key} data-testid={`health-rem-bucket-${b.key}`}>
                    <p className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 mb-1 ${b.tone}`}>
                        <b.icon size={10} weight="fill" /> {t(b.labelKey)}
                    </p>
                    <ul className="space-y-1.5">
                        {b.items.map((r) => (
                            <li key={r.id} className="rounded-xl border border-paw-line dark:border-white/10 p-2.5 flex items-center gap-2">
                                <span className="text-sm text-paw-ink dark:text-paw-bone flex-1 truncate">{r.title}</span>
                                <span className="text-[11px] text-paw-ink2 dark:text-paw-bone/60 flex-shrink-0">{_fmtDate(r.due_at, lang)}</span>
                                {r.status !== "completed" && (
                                    <button type="button" data-testid={`health-rem-complete-${r.id}`}
                                        onClick={async () => { await api.patch(`/reminders/${r.id}`, { status: "completed" }); await onChange(); }}
                                        className="text-[11px] font-semibold text-paw-forest dark:text-paw-terra hover:underline flex-shrink-0">
                                        {t("health.rem_mark_done")}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

/* ---------- Documents (reuses existing uploads) ---------- */
function DocumentsSection({ pet, onChange, onScan, t, lang }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef();

    const upload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("name", file.name);
            fd.append("kind", "other");
            fd.append("is_public", "false");
            await api.post(`/pets/${pet.id}/documents`, fd);
            await onChange();
        } catch { /* ignore, backend surfaces errors */ }
        finally { setUploading(false); }
    };

    const docs = pet.documents || [];
    return (
        <section data-testid="health-documents" className="rounded-3xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 sm:p-5 shadow-soft space-y-3">
            <div className="flex items-center gap-2">
                <FileText size={16} weight="fill" className="text-sky-500" />
                <h3 className="text-[11px] uppercase tracking-widest font-bold text-paw-ink dark:text-paw-bone flex-1">{t("health.sec_documents")}</h3>
                <button type="button" onClick={onScan} data-testid="health-doc-scan-btn"
                    className="inline-flex items-center gap-1 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white text-[11px] font-semibold px-3 h-7">
                    <Camera size={12} weight="fill" /> {t("health.doc_scan")}
                </button>
                <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
                    data-testid="health-doc-upload-btn"
                    className="inline-flex items-center gap-1 rounded-full border border-paw-line dark:border-white/15 text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 text-[11px] font-semibold px-3 h-7">
                    <UploadSimple size={12} weight="bold" /> {uploading ? t("health.uploading") : t("health.doc_upload")}
                </button>
                <input ref={inputRef} type="file" hidden accept="image/*,application/pdf" onChange={upload} />
            </div>
            {docs.length === 0 ? (
                <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 py-2" data-testid="health-doc-empty">{t("health.doc_empty")}</p>
            ) : (
                <ul className="space-y-2" data-testid="health-doc-list">
                    {docs.map((d) => (
                        <li key={d.id} className="flex items-center gap-3 rounded-2xl border border-paw-line dark:border-white/10 p-3">
                            <span className="w-9 h-9 rounded-2xl bg-sky-500/12 text-sky-700 dark:text-sky-300 grid place-items-center flex-shrink-0"><FileText size={16} weight="fill" /></span>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-paw-ink dark:text-paw-bone truncate">{d.name || t("health.doc_untitled")}</p>
                                <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60 truncate">{d.uploaded_at && _fmtDate(d.uploaded_at, lang)}</p>
                            </div>
                            <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer"
                                className="text-[11px] font-semibold text-paw-forest dark:text-paw-terra hover:underline flex-shrink-0">{t("health.view")}</a>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

/* ---------- Timeline ---------- */
function TimelineSection({ pet, t, lang }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/pets/${pet.id}/health-timeline`);
                setItems(data.items || []);
            } catch { setItems([]); }
        })();
    }, [pet.id, pet.updated_at]);
    if (items.length === 0) return null;
    return (
        <section data-testid="health-timeline" className="rounded-3xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 sm:p-5 shadow-soft">
            <h3 className="text-[11px] uppercase tracking-widest font-bold text-paw-ink dark:text-paw-bone mb-3 flex items-center gap-2">
                <Clock size={14} weight="fill" className="text-paw-forest dark:text-paw-terra" />
                {t("health.sec_timeline")}
            </h3>
            <ol className="relative space-y-3 ps-4 border-s border-paw-line dark:border-white/10">
                {items.map((i) => {
                    const Icon = i.kind === "vaccination" ? Syringe
                        : i.kind === "document" ? FileText
                        : i.kind === "appointment" ? Stethoscope
                        : Bell;
                    return (
                        <li key={`${i.kind}-${i.id}`} data-testid={`health-timeline-${i.kind}-${i.id}`} className="relative">
                            <span className="absolute -start-[22px] top-1 w-5 h-5 rounded-full bg-white dark:bg-white/10 border border-paw-line dark:border-white/15 grid place-items-center text-paw-forest dark:text-paw-terra"><Icon size={10} weight="fill" /></span>
                            <div>
                                <p className="text-sm font-semibold text-paw-ink dark:text-paw-bone">{i.title}</p>
                                <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60">{_fmtDate(i.at, lang)}{i.sub ? ` · ${i.sub}` : ""}</p>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

/* ---------- Scanner modal ---------- */
function DocumentScannerModal({ pet, onClose, onExtracted, t }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const camInputRef = useRef();
    const fileInputRef = useRef();

    const runExtract = async (file) => {
        if (!file) return;
        setBusy(true); setErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("kind_hint", "vaccine");
            // 1) Save the raw doc so it's preserved with the resulting record
            const savedDoc = await api.post(`/pets/${pet.id}/documents`,
                (() => { const f = new FormData(); f.append("file", file); f.append("name", file.name || "Scan"); f.append("kind", "vaccine"); f.append("is_public", "false"); return f; })(),
            );
            // 2) Extract with AI (never auto-saves)
            const { data } = await api.post(`/pets/${pet.id}/documents/extract`, fd);
            onExtracted({ ...data, document_id: savedDoc.data.id });
        } catch (e) {
            setErr(e?.response?.data?.detail || t("health.extract_error"));
        } finally { setBusy(false); }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-3 sm:p-6"
        >
            <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                data-testid="health-scanner-modal"
                className="w-full sm:max-w-md rounded-3xl bg-white dark:bg-[hsl(152,15%,10%)] border border-paw-line dark:border-white/10 p-5 space-y-3"
            >
                <div className="flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className="text-paw-terra" />
                    <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone flex-1">{t("health.scan_title")}</h2>
                    <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center text-paw-ink2"><X size={16} /></button>
                </div>
                <p className="text-xs text-paw-ink2 dark:text-paw-bone/70">{t("health.scan_sub")}</p>
                <div className="grid gap-2.5">
                    <button type="button" onClick={() => camInputRef.current?.click()} disabled={busy}
                        data-testid="health-scan-capture"
                        className="w-full rounded-2xl border border-paw-line dark:border-white/10 hover:bg-paw-muted dark:hover:bg-white/5 p-4 flex items-center gap-3 disabled:opacity-60">
                        <Camera size={20} weight="fill" className="text-paw-terra" />
                        <div className="text-start">
                            <p className="font-semibold text-sm text-paw-ink dark:text-paw-bone">{t("health.scan_camera")}</p>
                            <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60">{t("health.scan_camera_sub")}</p>
                        </div>
                    </button>
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}
                        data-testid="health-scan-upload"
                        className="w-full rounded-2xl border border-paw-line dark:border-white/10 hover:bg-paw-muted dark:hover:bg-white/5 p-4 flex items-center gap-3 disabled:opacity-60">
                        <UploadSimple size={20} weight="bold" className="text-paw-forest dark:text-paw-terra" />
                        <div className="text-start">
                            <p className="font-semibold text-sm text-paw-ink dark:text-paw-bone">{t("health.scan_upload")}</p>
                            <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60">{t("health.scan_upload_sub")}</p>
                        </div>
                    </button>
                </div>
                {busy && (
                    <div className="flex items-center justify-center gap-2 py-2 text-xs text-paw-ink2 dark:text-paw-bone/60">
                        <span className="w-4 h-4 rounded-full border-2 border-paw-terra/30 border-t-paw-terra animate-spin" />
                        {t("health.scanning")}
                    </div>
                )}
                {err && <p className="text-xs text-red-600" data-testid="health-scan-error">{err}</p>}
                <p className="text-[10px] text-paw-ink2 dark:text-paw-bone/50">{t("health.scan_privacy")}</p>
                <input ref={camInputRef}  type="file" hidden accept="image/*" capture="environment" onChange={(e) => { runExtract(e.target.files?.[0]); e.target.value = ""; }} />
                <input ref={fileInputRef} type="file" hidden accept="image/*,application/pdf"       onChange={(e) => { runExtract(e.target.files?.[0]); e.target.value = ""; }} />
            </motion.div>
        </motion.div>
    );
}

/* ---------- Extraction review modal ---------- */
function ExtractionReviewModal({ pet, payload, onClose, onSaved, t }) {
    const { fields = {}, confidence = {}, document_id } = payload || {};
    const [state, setState] = useState(() => ({
        name: (fields.vaccinations && fields.vaccinations[0]?.name) || fields.pet_name ? "" : "",
        // Prefer the first vaccination row from the model
        vacc_name:  (fields.vaccinations?.[0]?.name)          || "",
        dose_date:  (fields.vaccinations?.[0]?.dose_date)     || "",
        next_due:   (fields.vaccinations?.[0]?.next_due_date) || "",
        batch:      (fields.vaccinations?.[0]?.batch_number)  || "",
        vet:        fields.veterinarian || "",
        clinic:     fields.clinic       || "",
        microchip:  fields.microchip_id || "",
        passport:   fields.passport_id  || "",
        note:       (fields.vaccinations?.[0]?.note) || "",
    }));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const isLow = (key) => _isLowConfidence(confidence[key]);

    const save = async () => {
        setBusy(true); setErr("");
        try {
            // Save vaccination if we have at least a name
            if (state.vacc_name.trim()) {
                await api.post(`/pets/${pet.id}/vaccinations`, {
                    name: state.vacc_name.trim(),
                    dose_date: state.dose_date || undefined,
                    next_due_date: state.next_due || undefined,
                    batch_number: state.batch || undefined,
                    veterinarian: state.vet || undefined,
                    clinic: state.clinic || undefined,
                    note: state.note || undefined,
                    document_id: document_id || undefined,
                });
            }
            // Merge health identifiers only if they were extracted
            const healthPatch = {};
            if (state.microchip) healthPatch.microchip_id = state.microchip;
            if (state.passport)  healthPatch.passport_id  = state.passport;
            if (Object.keys(healthPatch).length) {
                await api.patch(`/pets/${pet.id}/health`, healthPatch);
            }
            await onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || t("health.save_error"));
        } finally { setBusy(false); }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-3 sm:p-6"
        >
            <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                data-testid="health-review-modal"
                className="w-full sm:max-w-lg rounded-3xl bg-white dark:bg-[hsl(152,15%,10%)] border border-paw-line dark:border-white/10 p-5 space-y-3 max-h-[85vh] overflow-y-auto"
            >
                <div className="flex items-center gap-2">
                    <Sparkle size={16} weight="fill" className="text-paw-terra" />
                    <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone flex-1">{t("health.review_title")}</h2>
                    <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center text-paw-ink2"><X size={16} /></button>
                </div>
                <p className="text-xs text-paw-ink2 dark:text-paw-bone/70">{t("health.review_sub")}</p>

                <ReviewField id="vacc_name" label={t("health.f_vacc_name")} value={state.vacc_name} low={isLow("vaccinations")} onChange={(v) => setState((s) => ({ ...s, vacc_name: v }))} t={t} />
                <TwoCol>
                    <ReviewField id="dose"  type="date" label={t("health.f_dose_date")} value={state.dose_date} low={isLow("vaccinations")} onChange={(v) => setState((s) => ({ ...s, dose_date: v }))} t={t} />
                    <ReviewField id="next"  type="date" label={t("health.f_next_due")}  value={state.next_due}  low={isLow("vaccinations")} onChange={(v) => setState((s) => ({ ...s, next_due: v }))} t={t} />
                </TwoCol>
                <TwoCol>
                    <ReviewField id="vet"    label={t("health.f_vet_name")}   value={state.vet}    low={isLow("veterinarian")} onChange={(v) => setState((s) => ({ ...s, vet: v }))} t={t} />
                    <ReviewField id="clinic" label={t("health.f_vet_clinic")} value={state.clinic} low={isLow("clinic")}       onChange={(v) => setState((s) => ({ ...s, clinic: v }))} t={t} />
                </TwoCol>
                <TwoCol>
                    <ReviewField id="microchip" label={t("health.f_microchip")}    value={state.microchip} low={isLow("microchip_id")} onChange={(v) => setState((s) => ({ ...s, microchip: v }))} t={t} />
                    <ReviewField id="passport"  label={t("health.f_passport_id")} value={state.passport}   low={isLow("passport_id")}  onChange={(v) => setState((s) => ({ ...s, passport: v }))} t={t} />
                </TwoCol>
                <ReviewField id="batch" label={t("health.f_batch")} value={state.batch} onChange={(v) => setState((s) => ({ ...s, batch: v }))} t={t} />
                <ReviewField id="note"  label={t("health.f_note")}  value={state.note}  onChange={(v) => setState((s) => ({ ...s, note: v }))} t={t} multiline />

                {err && <p className="text-xs text-red-600" data-testid="health-review-error">{err}</p>}

                <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={onClose} className="rounded-full border border-paw-line dark:border-white/15 px-4 h-10 text-sm">{t("health.discard")}</button>
                    <button type="button" onClick={save} disabled={busy}
                        data-testid="health-review-save"
                        className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-5 h-10 text-sm font-semibold disabled:opacity-60">
                        {busy ? t("health.saving") : t("health.review_save")}
                    </button>
                </div>
                <p className="text-[10px] text-paw-ink2 dark:text-paw-bone/50">{t("health.review_ai_note")}</p>
            </motion.div>
        </motion.div>
    );
}

function ReviewField({ id, label, value, onChange, type, low, multiline, t }) {
    const cls = `mt-1.5 w-full input-soft ${low ? "border-amber-500 focus:ring-amber-400/30" : ""}`;
    return (
        <label className="block">
            <span className={`label-soft flex items-center gap-1 ${low ? "text-amber-700 dark:text-amber-300" : ""}`}>
                {label}
                {low && <span data-testid={`review-low-${id}`} className="text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-800 dark:text-amber-300 px-1.5 py-0.5 rounded-full">{t("health.please_verify")}</span>}
            </span>
            {multiline ? (
                <textarea data-testid={`review-input-${id}`} rows={2} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
            ) : (
                <input data-testid={`review-input-${id}`} type={type || "text"} className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} />
            )}
        </label>
    );
}


/* =============================================================================
 *  Reminder confirm dialog  (shared by vaccinations and appointments)
 *
 *  All the fields are pre-filled from the calling source, and the user MUST
 *  press "Confirm reminder" for anything to be created. If the exact source
 *  already has a reminder, the backend silently returns the existing doc —
 *  we never insert a duplicate.
 * ============================================================================*/
function ReminderConfirmDialog({ pet, source, onClose, onCreated, t }) {
    // We coerce all inputs to strings so `value` never becomes `undefined`.
    const initDate = source.default_date || new Date().toISOString().slice(0, 10);
    const [state, setState] = useState({
        title: source.default_title || "",
        date: initDate,
        time: source.default_time || "09:00",
        note: source.default_note || "",
        offsets: ["1d"],
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const toggleOffset = (o) => {
        setState((s) => ({
            ...s,
            offsets: s.offsets.includes(o)
                ? s.offsets.filter((x) => x !== o)
                : [...s.offsets, o],
        }));
    };

    const confirm = async () => {
        if (!state.title.trim()) { setErr(t("health.rem_field_title")); return; }
        if (!state.date) { setErr(t("health.apt_date_req")); return; }
        setBusy(true); setErr("");
        try {
            const due = new Date(`${state.date}T${state.time || "09:00"}:00`);
            await api.post("/reminders", {
                pet_id: pet.id,
                type: source.reminder_type || "other",
                title: state.title.trim(),
                due_at: due.toISOString(),
                note: state.note || undefined,
                notify_offsets: state.offsets.length ? state.offsets : ["1d"],
                source_type: source.type,
                source_id: source.id,
            });
            await onCreated();
        } catch (e) {
            setErr(e?.response?.data?.detail || t("health.save_error"));
        } finally { setBusy(false); }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            // z-[10001] deliberately sits ABOVE the consent banner (z-[9999])
            // so the modal captures pointer events. `overflow-y-auto` and the
            // `pb-32` bottom cushion inside the panel guarantee the Confirm
            // button remains reachable at 390×844 even when the cookie
            // banner is still visible at the bottom of the viewport.
            className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-3 sm:p-6 overflow-y-auto overscroll-contain"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                data-testid="health-reminder-dialog"
                className="w-full sm:max-w-md rounded-3xl bg-white dark:bg-[hsl(152,15%,10%)] border border-paw-line dark:border-white/10 p-5 pb-6 space-y-3 max-h-[calc(100dvh-24px)] overflow-y-auto"
                style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px) + 88px)" }}
            >
                <div className="flex items-center gap-2">
                    <Bell size={16} weight="fill" className="text-amber-500" />
                    <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone flex-1">{t("health.rem_dialog_title")}</h2>
                    <button type="button" onClick={onClose} data-testid="health-reminder-close" className="w-8 h-8 grid place-items-center text-paw-ink2"><X size={16} /></button>
                </div>
                <p className="text-xs text-paw-ink2 dark:text-paw-bone/70">{t("health.rem_dialog_sub")}</p>

                <TextField id="rem_title" label={t("health.rem_field_title")}
                    value={state.title} onChange={(v) => setState((s) => ({ ...s, title: v }))} />
                <TwoCol>
                    <TextField id="rem_date" type="date" label={t("health.rem_field_date")}
                        value={state.date} onChange={(v) => setState((s) => ({ ...s, date: v }))} />
                    <TextField id="rem_time" type="time" label={t("health.rem_field_time")}
                        value={state.time} onChange={(v) => setState((s) => ({ ...s, time: v }))} />
                </TwoCol>
                <TextArea id="rem_note" label={t("health.rem_field_note")}
                    value={state.note} onChange={(v) => setState((s) => ({ ...s, note: v }))} />

                <fieldset className="rounded-xl border border-paw-line dark:border-white/10 p-3">
                    <legend className="text-[10px] uppercase tracking-widest font-bold text-paw-ink2 dark:text-paw-bone/60 px-1">{t("health.rem_field_notify")}</legend>
                    <div className="flex flex-wrap gap-2 pt-1">
                        {[
                            { key: "7d", label: t("health.rem_offset_7d") },
                            { key: "1d", label: t("health.rem_offset_1d") },
                            { key: "0d", label: t("health.rem_offset_0d") },
                        ].map((o) => (
                            <button
                                key={o.key} type="button"
                                data-testid={`health-rem-offset-${o.key}`}
                                onClick={() => toggleOffset(o.key)}
                                className={`text-[11px] rounded-full px-3 h-7 border transition ${
                                    state.offsets.includes(o.key)
                                        ? "bg-paw-forest text-white border-paw-forest dark:bg-paw-terra dark:border-paw-terra"
                                        : "bg-transparent text-paw-ink dark:text-paw-bone/80 border-paw-line dark:border-white/15"
                                }`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </fieldset>

                {err && <p className="text-xs text-red-600" data-testid="health-reminder-error">{err}</p>}
                <p className="text-[10px] text-paw-ink2 dark:text-paw-bone/50">{t("health.rem_never_auto")}</p>

                <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={onClose}
                        data-testid="health-reminder-cancel"
                        className="rounded-full border border-paw-line dark:border-white/15 px-4 h-10 text-sm">{t("health.cancel")}</button>
                    <button type="button" onClick={confirm} disabled={busy}
                        data-testid="health-reminder-confirm"
                        className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-5 h-10 text-sm font-semibold disabled:opacity-60">
                        {busy ? t("health.rem_confirming") : t("health.rem_confirm")}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}


/* =============================================================================
 *  Vet appointments section  (add / edit / complete / delete)
 * ============================================================================*/
function AppointmentSection({ pet, onChange, reminders, onReminderChange, t, lang }) {
    const [adding, setAdding] = useState(false);
    const list = pet.appointments || [];

    return (
        <section data-testid="health-appointments" className="rounded-3xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.03] p-4 sm:p-5 shadow-soft space-y-3">
            <div className="flex items-center gap-2">
                <Stethoscope size={16} weight="fill" className="text-paw-forest dark:text-paw-terra" />
                <h3 className="text-[11px] uppercase tracking-widest font-bold text-paw-ink dark:text-paw-bone flex-1">{t("health.sec_appointments")}</h3>
                <button
                    type="button" onClick={() => setAdding((v) => !v)}
                    data-testid="health-apt-add-toggle"
                    className="inline-flex items-center gap-1 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white text-[11px] font-semibold px-3 h-7"
                >
                    <Plus size={12} weight="bold" />
                    {t("health.apt_add")}
                </button>
            </div>

            {adding && (
                <AppointmentForm
                    pet={pet}
                    onSaved={async () => { setAdding(false); await onChange(); }}
                    onCancel={() => setAdding(false)}
                    onReminderChange={onReminderChange}
                    t={t}
                />
            )}

            {list.length === 0 ? (
                <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 py-2" data-testid="health-apt-empty">{t("health.apt_empty")}</p>
            ) : (
                <ul className="space-y-2" data-testid="health-apt-list">
                    {list.map((a) => (
                        <AppointmentRow
                            key={a.id} pet={pet} apt={a}
                            onChange={onChange}
                            reminders={reminders}
                            onReminderChange={onReminderChange}
                            t={t} lang={lang}
                        />
                    ))}
                </ul>
            )}
        </section>
    );
}

function AppointmentForm({ pet, prefill = {}, onSaved, onCancel, onReminderChange, t, existingId }) {
    const [state, setState] = useState(() => ({
        date: prefill.date || "",
        time: prefill.time || "",
        clinic: prefill.clinic || "",
        veterinarian: prefill.veterinarian || "",
        reason: prefill.reason || "",
        notes: prefill.notes || "",
    }));
    const [alsoReminder, setAlsoReminder] = useState(false);
    const [pendingReminder, setPendingReminder] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const save = async () => {
        if (!state.date) { setErr(t("health.apt_date_req")); return; }
        setBusy(true); setErr("");
        try {
            const payload = Object.fromEntries(Object.entries(state).filter(([, v]) => v !== ""));
            let saved;
            if (existingId) {
                const r = await api.patch(`/pets/${pet.id}/appointments/${existingId}`, payload);
                saved = r.data;
            } else {
                const r = await api.post(`/pets/${pet.id}/appointments`, payload);
                saved = r.data;
            }
            await onSaved();
            // If the user opted in, open the reminder confirmation dialog
            // (still requires an explicit "Confirm reminder" tap).
            if (alsoReminder && !existingId) {
                setPendingReminder({
                    type: "vet_appointment",
                    id: saved.id,
                    reminder_type: "vet_visit",
                    default_title: state.reason || state.clinic || t("health.apt_reason_default"),
                    default_date: state.date,
                    default_time: state.time || "09:00",
                    default_note: state.clinic || "",
                });
            }
        } catch (e) {
            setErr(e?.response?.data?.detail || t("health.save_error"));
        } finally { setBusy(false); }
    };

    return (
        <div className="rounded-2xl border border-paw-terra/25 bg-paw-terra/6 p-3 space-y-2" data-testid="health-apt-form">
            <TwoCol>
                <TextField id="apt_date" type="date" label={t("health.apt_f_date")} value={state.date} onChange={(v) => setState((s) => ({ ...s, date: v }))} />
                <TextField id="apt_time" type="time" label={t("health.apt_f_time")} value={state.time} onChange={(v) => setState((s) => ({ ...s, time: v }))} />
            </TwoCol>
            <TwoCol>
                <TextField id="apt_clinic" label={t("health.apt_f_clinic")} value={state.clinic} onChange={(v) => setState((s) => ({ ...s, clinic: v }))} />
                <TextField id="apt_vet" label={t("health.apt_f_vet")} value={state.veterinarian} onChange={(v) => setState((s) => ({ ...s, veterinarian: v }))} />
            </TwoCol>
            <TextField id="apt_reason" label={t("health.apt_f_reason")} placeholder={t("health.apt_f_reason_ph")} value={state.reason} onChange={(v) => setState((s) => ({ ...s, reason: v }))} />
            <TextArea id="apt_notes" label={t("health.apt_f_notes")} placeholder={t("health.apt_f_notes_ph")} value={state.notes} onChange={(v) => setState((s) => ({ ...s, notes: v }))} />

            {!existingId && (
                <label className="flex items-center gap-2 text-xs text-paw-ink dark:text-paw-bone select-none" data-testid="health-apt-also-reminder-label">
                    <input
                        type="checkbox"
                        data-testid="health-apt-also-reminder"
                        checked={alsoReminder}
                        onChange={(e) => setAlsoReminder(e.target.checked)}
                        className="rounded"
                    />
                    <span>{t("health.apt_also_reminder")}</span>
                </label>
            )}

            {err && <p className="text-xs text-red-600" data-testid="health-apt-error">{err}</p>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onCancel} className="rounded-full border border-paw-line dark:border-white/15 px-4 h-9 text-xs">{t("health.cancel")}</button>
                <button type="button" onClick={save} disabled={busy}
                    data-testid="health-apt-save"
                    className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-4 h-9 text-xs font-semibold disabled:opacity-60">
                    {busy ? t("health.saving") : t("health.save")}
                </button>
            </div>

            <AnimatePresence>
                {pendingReminder && (
                    <ReminderConfirmDialog
                        pet={pet}
                        source={pendingReminder}
                        onClose={() => setPendingReminder(null)}
                        onCreated={async () => { setPendingReminder(null); await onReminderChange(); }}
                        t={t}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function AppointmentRow({ pet, apt, onChange, reminders, onReminderChange, t, lang }) {
    const [editing, setEditing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [dlg, setDlg] = useState(false);

    const linkedReminder = useMemo(() => {
        const all = [
            ...(reminders?.upcoming || []),
            ...(reminders?.overdue || []),
            ...(reminders?.completed || []),
        ];
        return all.find((r) => r.source_type === "vet_appointment" && r.source_id === apt.id) || null;
    }, [reminders, apt.id]);

    const remove = async () => {
        if (!window.confirm(t("health.apt_confirm_delete"))) return;
        setBusy(true);
        try { await api.delete(`/pets/${pet.id}/appointments/${apt.id}`); await onChange(); }
        finally { setBusy(false); }
    };

    const markDone = async () => {
        setBusy(true);
        try { await api.post(`/pets/${pet.id}/appointments/${apt.id}/complete`); await onChange(); }
        finally { setBusy(false); }
    };

    if (editing) {
        return (
            <li data-testid={`health-apt-${apt.id}`}>
                <AppointmentForm
                    pet={pet}
                    prefill={apt}
                    existingId={apt.id}
                    onSaved={async () => { setEditing(false); await onChange(); }}
                    onCancel={() => setEditing(false)}
                    onReminderChange={onReminderChange}
                    t={t}
                />
            </li>
        );
    }

    const statusTone = apt.status === "completed"
        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
        : apt.status === "cancelled"
            ? "bg-neutral-500/15 text-neutral-600 dark:text-neutral-300"
            : "bg-amber-500/15 text-amber-800 dark:text-amber-300";
    const statusLabel =
        apt.status === "completed" ? t("health.apt_status_completed")
        : apt.status === "cancelled" ? t("health.apt_status_cancelled")
        : t("health.apt_status_scheduled");

    const canSuggestReminder = apt.status === "scheduled" && !linkedReminder;

    return (
        <li data-testid={`health-apt-${apt.id}`} className="rounded-2xl border border-paw-line dark:border-white/10 p-3 space-y-2">
            <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-2xl bg-paw-forest/12 text-paw-forest dark:bg-paw-terra/20 dark:text-paw-terra grid place-items-center flex-shrink-0">
                    <CalendarBlank size={16} weight="fill" />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-paw-ink dark:text-paw-bone truncate flex-1">
                            {apt.reason || apt.clinic || t("health.apt_reason_default")}
                        </p>
                        <span className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 font-bold ${statusTone}`}>{statusLabel}</span>
                    </div>
                    <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60 truncate">
                        {_fmtDate(apt.date, lang)}
                        {apt.time && <span> · {apt.time}</span>}
                        {apt.veterinarian && <span> · {apt.veterinarian}</span>}
                        {apt.clinic && <span> · {apt.clinic}</span>}
                    </p>
                </div>
                {apt.status === "scheduled" && (
                    <button type="button" onClick={markDone} disabled={busy}
                        data-testid={`health-apt-complete-${apt.id}`}
                        className="w-8 h-8 rounded-full text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 grid place-items-center flex-shrink-0"
                        title={t("health.apt_mark_done")}>
                        <CheckCircle size={16} weight="fill" />
                    </button>
                )}
                <button type="button" onClick={() => setEditing(true)} disabled={busy}
                    data-testid={`health-apt-edit-${apt.id}`}
                    className="w-8 h-8 rounded-full text-paw-forest dark:text-paw-terra hover:bg-paw-muted dark:hover:bg-white/5 grid place-items-center flex-shrink-0"
                    title={t("health.apt_edit")}>
                    <PencilSimple size={14} />
                </button>
                <button type="button" onClick={remove} disabled={busy}
                    data-testid={`health-apt-remove-${apt.id}`}
                    className="w-8 h-8 rounded-full text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 grid place-items-center flex-shrink-0"
                    title={t("health.apt_delete")}>
                    <Trash size={14} />
                </button>
            </div>

            {canSuggestReminder && (
                <div
                    className="rounded-xl bg-amber-500/10 border border-amber-500/25 px-3 py-2 flex items-center gap-2"
                    data-testid={`health-apt-remind-suggest-${apt.id}`}
                >
                    <Bell size={14} weight="fill" className="text-amber-600 dark:text-amber-300 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-amber-900 dark:text-amber-100 truncate">{t("health.rem_suggest_h_appointment")}</p>
                        <p className="text-[10px] text-amber-800/80 dark:text-amber-200/70 truncate">{t("health.rem_suggest_sub")}</p>
                    </div>
                    <button
                        type="button" onClick={() => setDlg(true)}
                        data-testid={`health-apt-remind-open-${apt.id}`}
                        className="rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold px-3 h-7 flex-shrink-0"
                    >
                        {t("health.rem_suggest_open")}
                    </button>
                </div>
            )}
            {linkedReminder && (
                <p
                    className="text-[10.5px] text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"
                    data-testid={`health-apt-remind-linked-${apt.id}`}
                >
                    <CheckCircle size={12} weight="fill" /> {t("health.rem_suggest_existing")} · {_fmtDate(linkedReminder.due_at, lang)}
                </p>
            )}

            <AnimatePresence>
                {dlg && (
                    <ReminderConfirmDialog
                        pet={pet}
                        source={{
                            type: "vet_appointment",
                            id: apt.id,
                            reminder_type: "vet_visit",
                            default_title: apt.reason || apt.clinic || t("health.apt_reason_default"),
                            default_date: apt.date,
                            default_time: apt.time || "09:00",
                            default_note: apt.clinic || "",
                        }}
                        onClose={() => setDlg(false)}
                        onCreated={async () => { setDlg(false); await onReminderChange(); }}
                        t={t}
                    />
                )}
            </AnimatePresence>
        </li>
    );
}
